import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Alert,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  TextField
} from '@mui/material';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ru } from 'date-fns/locale/ru';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import CloudIcon from '@mui/icons-material/Cloud';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import UmbrellaIcon from '@mui/icons-material/Umbrella';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import FilterDramaIcon from '@mui/icons-material/FilterDrama';
import { Arena, TimeSlot, DailyScheduleResponse, ActiveCourtsResponse, CourtSchedule, ActiveCourt } from '../types/courts';
import { API_ENDPOINTS, TENNIS_RU_BASE_URL } from '../api/config';
import axiosInstance, { tennisInstance } from '../api/axios';

interface WeatherData {
  temp: number;
  weathercode: number;
  description: string;
  forecast: {
    time: string;
    weathercode: number;
  }[];
}

interface CourtRecommendation {
  type: 'success' | 'warning' | 'error';
  message: string;
}

interface OutdoorCourtsResponse {
  result: boolean;
  phones?: string[];
  errorCode?: number;
  error?: string;
}

interface PhonesOutdoorResponse {
  result: boolean;
  phones?: string[];
  errorCode?: number;
  error?: string;
}

const Courts: React.FC = () => {
  const [arenas, setArenas] = useState<Arena[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [selectedArenaId, setSelectedArenaId] = useState<number>(5);
  const [isCoverDialogOpen, setIsCoverDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [areCourtsClosed, setAreCourtsClosed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [partnerId, setPartnerId] = useState<string>('79a3b7f5-20ec-11eb-80c3-ac1f6bb3f7d3'); // Default partner ID
  const [activeCourts, setActiveCourts] = useState<ActiveCourt[]>([]);
  
  // Новые состояния для выбора времени
  const [blockStartDate, setBlockStartDate] = useState<Date>(new Date());
  const [blockStartTime, setBlockStartTime] = useState<Date>(new Date());
  const [blockEndDate, setBlockEndDate] = useState<Date>(new Date());
  const [blockEndTime, setBlockEndTime] = useState<Date>(new Date());
  const [useTimeRange, setUseTimeRange] = useState(false);
  // Предпросмотр телефонов арендаторов уличных кортов на выбранную дату
  const [affectedPhones, setAffectedPhones] = useState<string[]>([]);
  const [isLoadingPhones, setIsLoadingPhones] = useState(false);

  // CSS анимация для пульсации
  const pulseAnimation = {
    '@keyframes pulse': {
      '0%': {
        boxShadow: '0 0 0 0 rgba(255, 152, 0, 0.7)',
      },
      '70%': {
        boxShadow: '0 0 0 10px rgba(255, 152, 0, 0)',
      },
      '100%': {
        boxShadow: '0 0 0 0 rgba(255, 152, 0, 0)',
      },
    },
  };

  const getWeatherIcon = (code: number) => {
    if (code === 0) return <WbSunnyIcon sx={{ fontSize: 48, color: '#1976d2' }} />;
    if (code === 1 || code === 2) return <CloudQueueIcon sx={{ fontSize: 48, color: '#1976d2' }} />;
    if (code === 3) return <CloudIcon sx={{ fontSize: 48, color: '#1976d2' }} />;
    if ((code >= 51 && code <= 65) || code === 95) return <UmbrellaIcon sx={{ fontSize: 48, color: '#1976d2' }} />;
    if (code >= 71 && code <= 75) return <AcUnitIcon sx={{ fontSize: 48, color: '#1976d2' }} />;
    if (code === 45 || code === 48) return <FilterDramaIcon sx={{ fontSize: 48, color: '#1976d2' }} />;
    return <WbSunnyIcon sx={{ fontSize: 48, color: '#1976d2' }} />;
  };

  const getWeatherDescription = (code: number): string => {
    const descriptions: { [key: number]: string } = {
      0: 'Ясно',
      1: 'Преимущественно ясно',
      2: 'Переменная облачность',
      3: 'Пасмурно',
      45: 'Туман',
      48: 'Туман с инеем',
      51: 'Легкая морось',
      53: 'Умеренная морось',
      55: 'Сильная морось',
      61: 'Небольшой дождь',
      63: 'Умеренный дождь',
      65: 'Сильный дождь',
      71: 'Небольшой снег',
      73: 'Умеренный снег',
      75: 'Сильный снег',
      95: 'Гроза',
    };
    return descriptions[code] || 'Ясно';
  };

  const isRainyWeather = (code: number) => {
    return (code >= 51 && code <= 65) || code === 95;
  };

  const isCloudyWeather = (code: number) => {
    return code === 2 || code === 3;
  };

  const getCourtRecommendation = (forecast: WeatherData['forecast']): CourtRecommendation => {
    // Проверяем, есть ли дождь в ближайшие 3 часа
    if (forecast.some(hour => isRainyWeather(hour.weathercode))) {
      return {
        type: 'error',
        message: 'Внимание! В ближайшие 3 часа ожидается дождь. Рекомендуется перекрыть уличные корты.'
      };
    }
    
    // Проверяем, есть ли облачность
    if (forecast.some(hour => isCloudyWeather(hour.weathercode))) {
      return {
        type: 'warning',
        message: 'Погода переменчивая. Будьте внимательны к состоянию уличных кортов.'
      };
    }
    
    return {
      type: 'success',
      message: 'В ближайшие 3 часа ожидается прекрасная погода для игры в теннис!'
    };
  };

  const checkCourtsStatus = (arena: Arena) => {
    if (!arena || !arena.courts.length) return false;
    
    let hasAvailableSlots = false;
    
    // Проверяем все слоты (кроме 07:00 и 23:00)
    arena.courts.forEach(court => {
      const courtSlots = getCourtTimeSlots(court.id, arena);
      courtSlots.forEach(slot => {
        const slotTime = slot.time;
        
        // Пропускаем слоты 07:00 и 23:00
        if (slotTime === '07:00' || slotTime === '23:00') {
          return;
        }
        
        // Если слот свободен
        if (isSlotAvailable(court.id, slot.id, arena)) {
          hasAvailableSlots = true;
        }
      });
    });
    
    // Если нет свободных слотов (кроме 07:00 и 23:00) - корты закрыты
    return !hasAvailableSlots;
  };

  const fetchActiveCourts = async () => {
    try {
      const response = await tennisInstance.get(API_ENDPOINTS.activeCourts);
      const data: ActiveCourtsResponse = response.data;
      if (data.result && data.activeCourts) {
        // Фильтруем только уличные корты (грунт, Padel (улица) и т.п.)
        setActiveCourts(data.activeCourts.filter(court => court.courtLocation === 'Улица'));
      } else {
        setActiveCourts([]);
      }
    } catch (error) {
      setActiveCourts([]);
    }
  };

  const fetchAffectedPhones = async () => {
    setIsLoadingPhones(true);
    try {
      const checkDate = selectedDate.toISOString().split('T')[0] + 'T00:00:00';
      const response = await tennisInstance.get(
        API_ENDPOINTS.phonesOutdoorCourts(checkDate).replace(TENNIS_RU_BASE_URL, '')
      );
      const data: PhonesOutdoorResponse = response.data;
      setAffectedPhones(data.result && data.phones ? data.phones : []);
    } catch (error) {
      setAffectedPhones([]);
    } finally {
      setIsLoadingPhones(false);
    }
  };

  const fetchCourtsData = async () => {
    try {
      // Format date for tennis.ru API (YYYY-MM-DDTHH:mm:ss)
      const checkDate = selectedDate.toISOString().split('T')[0] + 'T00:00:00';
      // Получаем расписание и активные корты параллельно
      const [scheduleResp, activeResp] = await Promise.all([
        tennisInstance.get(API_ENDPOINTS.dailySchedule(partnerId, checkDate)),
        tennisInstance.get(API_ENDPOINTS.activeCourts)
      ]);
      const scheduleData: DailyScheduleResponse = scheduleResp.data;
      const activeData: ActiveCourtsResponse = activeResp.data;

      // Получаем id всех уличных кортов
      const outdoorCourtIds = (activeData.activeCourts || [])
        .filter(court => court.courtLocation === 'Улица')
        .map(court => court.courtId);

      // Фильтруем расписание только по этим id
      const onlyOutdoor = (scheduleData.schedule || []).filter(
        (courtSchedule: CourtSchedule) => outdoorCourtIds.includes(courtSchedule.courtId)
      );

      if (onlyOutdoor.length > 0) {
        // Мапа courtId -> courtName для быстрого поиска
        const courtIdToName = (activeData.activeCourts || [])
          .filter(court => court.courtLocation === 'Улица')
          .reduce((acc, court) => {
            acc[court.courtId] = court.courtName;
            return acc;
          }, {} as Record<string, string>);

        const convertedArena: Arena = {
          id: 5,
          title: 'Уличные корты',
          cell_color: '#1976d2',
          courts: onlyOutdoor.map((courtSchedule: CourtSchedule) => ({
            id: parseInt(courtSchedule.courtId, 36),
            title: courtIdToName[courtSchedule.courtId] || `Корт ${courtSchedule.courtId}`,
            arena_id: 5,
            created_at: null,
            updated_at: null,
            active: 1,
            cell_color: '#1976d2',
            sort: 0
          })),
          seats: onlyOutdoor.flatMap((courtSchedule: CourtSchedule) =>
            courtSchedule.rentInfo.map((rentInfo, index) => ({
              id: index + 1,
              time: rentInfo.startTime.split('T')[1].slice(0, 5),
              price: rentInfo.price,
              price_weekend: rentInfo.price,
              court_id: parseInt(courtSchedule.courtId, 36),
              is_special_price: false
            }))
          ),
          unAvailableSeats: onlyOutdoor.reduce((acc, courtSchedule) => {
            const unavailableSlots = courtSchedule.rentInfo
              .map((rentInfo, index) => rentInfo.busy ? index + 1 : null)
              .filter(slotId => slotId !== null) as number[];
            if (unavailableSlots.length > 0) {
              acc[parseInt(courtSchedule.courtId, 36)] = unavailableSlots;
            }
            return acc;
          }, {} as { [key: string]: number[] })
        };
        setArenas([convertedArena]);
        setAreCourtsClosed(checkCourtsStatus(convertedArena));
      } else {
        setArenas([{
          id: 5,
          title: 'Уличные корты',
          courts: [],
          seats: [],
          unAvailableSeats: {},
          cell_color: '#1976d2'
        }]);
        setAreCourtsClosed(false);
      }
    } catch (error) {
      setArenas([{
        id: 5,
        title: 'Уличные корты',
        courts: [],
        seats: [],
        unAvailableSeats: {},
        cell_color: '#1976d2'
      }]);
      setAreCourtsClosed(false);
    }
  };

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const response = await axiosInstance.get(
          `https://api.open-meteo.com/v1/forecast?latitude=55.569918&longitude=37.410226&current=temperature_2m,weathercode&hourly=weathercode&forecast_hours=4`
        );
        
        const currentHour = new Date().getHours();
        const nextThreeHours = response.data.hourly.weathercode
          .slice(currentHour, currentHour + 4)
          .map((code: number, index: number) => ({
            time: new Date(response.data.hourly.time[currentHour + index]).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
            weathercode: code
          }));

        setWeather({
          temp: Math.round(response.data.current.temperature_2m),
          weathercode: response.data.current.weathercode,
          description: getWeatherDescription(response.data.current.weathercode),
          forecast: nextThreeHours
        });
      } catch (error) {
        console.error('Error fetching weather:', error);
      }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 300000); // Обновляем каждые 5 минут
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchCourtsData();
    fetchActiveCourts();
  }, [selectedDate, partnerId]);

  const isWeekend = (date: Date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const getPrice = (price: number, priceWeekend: number) => {
    return isWeekend(selectedDate) ? priceWeekend : price;
  };

  const formatTime = (time: string) => {
    return time.slice(0, 5);
  };

  const isSlotAvailable = (courtId: number, slotId: number, arena: Arena) => {
    return !arena.unAvailableSeats[courtId]?.includes(slotId);
  };

  const getCourtTimeSlots = (courtId: number, arena: Arena) => {
    return arena.seats.filter(slot => slot.court_id === courtId);
  };

  const resetDialogState = () => {
    const now = new Date();
    const startTime = new Date();
    startTime.setHours(now.getHours(), 0, 0, 0);
    
    const endTime = new Date();
    endTime.setHours(now.getHours() + 1, 0, 0, 0);
    
    setBlockStartDate(new Date());
    setBlockStartTime(startTime);
    setBlockEndDate(new Date());
    setBlockEndTime(endTime);
    setUseTimeRange(false);
  };

  const handleCoverCourts = async () => {
    setIsLoading(true);
    try {
      const isBlock = areCourtsClosed; // True для блокировки, False для разблокировки
      
      let startDate: string | undefined;
      let finishDate: string | undefined;
      
      if (useTimeRange) {
        // Форматируем даты и время для API (до начала часа)
        const startDateTime = new Date(blockStartDate);
        startDateTime.setHours(blockStartTime.getHours(), 0, 0, 0);
        
        const endDateTime = new Date(blockEndDate);
        endDateTime.setHours(blockEndTime.getHours(), 0, 0, 0);
        
        // Форматируем в локальное время пользователя
        startDate = startDateTime.toLocaleString('sv-SE', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }).replace(' ', 'T');
        finishDate = endDateTime.toLocaleString('sv-SE', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }).replace(' ', 'T');
      } else if (areCourtsClosed) {
        // Для полной блокировки: startDate = текущий час, finishDate = не передаем (будет до следующего дня)
        const now = new Date();
        now.setMinutes(0, 0, 0); // Устанавливаем на начало текущего часа
        startDate = now.toLocaleString('sv-SE', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }).replace(' ', 'T');
        // finishDate не передаем - будет до начала следующего дня
      } else {
        // Для полного открытия: startDate = текущий час
        const now = new Date();
        now.setMinutes(0, 0, 0);
        startDate = now.toLocaleString('sv-SE', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        }).replace(' ', 'T');
        // finishDate не передаем
      }
      
      // Вызываем tennis.ru API с авторизацией
      const tennisRuUrl = API_ENDPOINTS.outdoorCourtsBlocking(isBlock, startDate, finishDate);
      console.log('Tennis.ru URL:', tennisRuUrl);
      console.log('Parameters:', { isBlock, startDate, finishDate });

      const tennisResponse = await tennisInstance.get(tennisRuUrl.replace(TENNIS_RU_BASE_URL, ''));
      const tennisData: OutdoorCourtsResponse = tennisResponse.data;

      if (!tennisData.result) {
        throw new Error(tennisData.error || 'Ошибка при обращении к tennis.ru');
      }

      // Если получили телефоны затронутых арендаторов — рассылаем SMS/Email через backend.
      // Токен подставляется интерсептором axiosInstance (Bearer).
      let notifiedCount = 0;
      if (tennisData.phones && tennisData.phones.length > 0) {
        try {
          await axiosInstance.post(API_ENDPOINTS.courtStatusNotify, {
            mode: areCourtsClosed ? 'close' : 'open',
            phones: tennisData.phones,
          });
          notifiedCount = tennisData.phones.length;
        } catch (notifyError) {
          // Блокировка на tennis.ru уже выполнена — не валим весь флоу из-за неудачной рассылки
          console.error('Не удалось отправить уведомления:', notifyError);
        }
      }

      const baseMessage = areCourtsClosed ? 'Корты успешно перекрыты' : 'Корты успешно открыты';
      setSnackbarMessage(
        notifiedCount > 0
          ? `${baseMessage}. Уведомления отправлены: ${notifiedCount}`
          : baseMessage
      );
      setSnackbarOpen(true);
      setIsCoverDialogOpen(false);
      
      // Обновляем данные кортов после успешного запроса
      await fetchCourtsData();
    } catch (error) {
      console.error('Error:', error);
      setSnackbarMessage(`Произошла ошибка: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        <div style={{
          backgroundColor: '#f5f5f5',
          borderRadius: '12px',
          padding: '20px',
          height: 'fit-content',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <Typography variant="h6" style={{ marginBottom: '16px', color: '#1976d2' }}>
            Выбор арены
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="arena-select-label">Выберите арену</InputLabel>
            <Select
              labelId="arena-select-label"
              value={selectedArenaId}
              label="Выберите арену"
              onChange={(e) => setSelectedArenaId(e.target.value as number)}
              sx={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1976d2',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1976d2',
                },
              }}
            >
              {arenas.map((arena) => (
                <MenuItem key={arena.id} value={arena.id}>
                  {arena.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div style={{
          backgroundColor: '#f5f5f5',
          borderRadius: '12px',
          padding: '20px',
          height: 'fit-content',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <Typography variant="h6" style={{ marginBottom: '16px', color: '#1976d2' }}>
            Выбор даты
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
            <DatePicker
              value={selectedDate}
              onChange={(newDate) => newDate && setSelectedDate(newDate)}
              format="dd.MM.yyyy"
              minDate={new Date()}
              maxDate={new Date(new Date().setDate(new Date().getDate() + 1))}
              sx={{
                width: '100%',
                '& .MuiInputBase-root': {
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                }
              }}
            />
          </LocalizationProvider>
        </div>

        {weather && (
          <>
            <div style={{
              backgroundColor: '#f5f5f5',
              borderRadius: '12px',
              padding: '20px',
              height: 'fit-content',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <Typography variant="h6" style={{ marginBottom: '16px', color: '#1976d2' }}>
                Прогноз погоды
              </Typography>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                backgroundColor: '#fff',
                padding: '20px',
                borderRadius: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                  padding: '0 0 16px 0',
                  borderBottom: '1px solid #eee'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                  }}>
                    {getWeatherIcon(weather.weathercode)}
                  </div>
                  <div>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '4px' }}>
                      {weather.temp}°C
                    </div>
                    <div style={{ color: '#666', fontSize: '16px' }}>{weather.description}</div>
                  </div>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
                  gap: '12px',
                  justifyContent: 'space-between'
                }}>
                  {weather.forecast.map((hour, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 8px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        transition: 'transform 0.2s ease',
                        cursor: 'pointer',
                        transform: 'translateY(0)',
                        '&:hover': {
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <div style={{ 
                        fontSize: '14px', 
                        color: '#666', 
                        fontWeight: '500'
                      }}>
                        {hour.time}
                      </div>
                      {getWeatherIcon(hour.weathercode)}
                      <div style={{ 
                        fontSize: '13px', 
                        color: '#666',
                        textAlign: 'center' 
                      }}>
                        {getWeatherDescription(hour.weathercode)}
                      </div>
                    </Box>
                  ))}
                </div>
              </div>
            </div>

            <div style={{
              backgroundColor: '#f5f5f5',
              borderRadius: '12px',
              padding: '20px',
              height: 'fit-content',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <Typography variant="h6" style={{ marginBottom: '16px', color: '#1976d2' }}>
                Рекомендации
              </Typography>
              <Alert 
                severity={getCourtRecommendation(weather.forecast).type}
                style={{
                  fontSize: '14px',
                  borderRadius: '8px'
                }}
                icon={false}
              >
                {getCourtRecommendation(weather.forecast).message}
              </Alert>
            </div>
          </>
        )}
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <Typography variant="h4">Расписание кортов</Typography>
        {selectedArenaId === 5 && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setAreCourtsClosed(false);
                resetDialogState();
                fetchAffectedPhones();
                setIsCoverDialogOpen(true);
              }}
              sx={{
                backgroundColor: '#4caf50',
                '&:hover': {
                  backgroundColor: '#43a047',
                },
              }}
            >
              Открыть корты
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setAreCourtsClosed(true);
                resetDialogState();
                fetchAffectedPhones();
                setIsCoverDialogOpen(true);
              }}
              sx={{
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
              }}
            >
              Перекрыть корты
            </Button>
          </Box>
        )}
      </div>

      <div style={{
        width: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        msOverflowStyle: '-ms-autohiding-scrollbar',
        position: 'relative',
        backgroundColor: '#f5f5f5',
        padding: '20px',
        borderRadius: '8px'
      }}>
        <div style={{
          display: 'flex',
          gap: '20px',
          minWidth: 'min-content',
          paddingBottom: '12px'
        }}>
          {arenas
            .filter(arena => !selectedArenaId || arena.id === selectedArenaId)
            .flatMap(arena => 
              arena.courts.map((court) => (
                <div
                  key={`${arena.id}-${court.id}`}
                  style={{
                    width: '280px',
                    flex: '0 0 280px',
                    backgroundColor: court.cell_color,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <div style={{
                    padding: '16px',
                    color: '#fff',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {court.title}
                  </div>
                  <div style={{
                    backgroundColor: '#fff',
                    padding: '12px',
                    flex: 1
                  }}>
                    {getCourtTimeSlots(court.id, arena).map((slot: TimeSlot) => (
                      <div
                        key={slot.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '60px 1fr auto',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '10px',
                          borderBottom: '1px solid #e0e0e0',
                          backgroundColor: isSlotAvailable(court.id, slot.id, arena) ? 'transparent' : '#fafafa',
                          transition: 'background-color 0.2s ease',
                          borderRadius: '4px',
                          margin: '4px 0'
                        }}
                      >
                        <div style={{
                          fontSize: '15px',
                          fontWeight: '500',
                          color: '#333'
                        }}>
                          {formatTime(slot.time)}
                        </div>
                        <div style={{
                          fontSize: '15px',
                          fontWeight: '600',
                          color: '#1976d2',
                          textAlign: 'right'
                        }}>
                          {getPrice(slot.price, slot.price_weekend)}
                          <span style={{ fontSize: '13px', marginLeft: '2px', color: '#666' }}>р/ч</span>
                        </div>
                        <Chip
                          label={isSlotAvailable(court.id, slot.id, arena) ? 'Свободно' : 'Занято'}
                          color={isSlotAvailable(court.id, slot.id, arena) ? 'success' : 'error'}
                          size="small"
                          sx={{
                            height: '24px',
                            fontSize: '12px',
                            minWidth: '80px',
                            '& .MuiChip-label': {
                              padding: '0 8px'
                            }
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
        </div>
      </div>

      <Dialog
        open={isCoverDialogOpen}
        onClose={() => setIsCoverDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '20px',
          }
        }}
      >
        <DialogTitle sx={{ color: '#1976d2', fontWeight: 'bold' }}>
          {areCourtsClosed ? 'Перекрытие кортов' : 'Открытие кортов'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ marginBottom: '20px' }}>
            {areCourtsClosed
              ? 'Выберите параметры для перекрытия уличных кортов.'
              : 'Выберите параметры для открытия уличных кортов.'}
          </Typography>

          <Alert
            severity={affectedPhones.length > 0 ? 'info' : 'success'}
            sx={{ marginBottom: '20px', borderRadius: '8px' }}
          >
            {isLoadingPhones
              ? 'Загружаем список затронутых арендаторов…'
              : affectedPhones.length > 0
                ? `Уведомления получат арендаторов: ${affectedPhones.length}`
                : 'На выбранную дату арендаторов уличных кортов не найдено'}
          </Alert>

          <Box sx={{ marginBottom: '20px' }}>
            <FormControl fullWidth>
              <InputLabel>Тип операции</InputLabel>
              <Select
                value={useTimeRange ? 'range' : 'full'}
                onChange={(e) => setUseTimeRange(e.target.value === 'range')}
                label="Тип операции"
              >
                <MenuItem value="full">
                  {areCourtsClosed 
                    ? 'Полная блокировка (с текущего часа до завтра)' 
                    : 'Полное открытие (с текущего часа)'}
                </MenuItem>
                <MenuItem value="range">
                  {areCourtsClosed 
                    ? 'Блокировка по времени (с указанием часов)' 
                    : 'Открытие по времени (с указанием часов)'}
                </MenuItem>
              </Select>
            </FormControl>
            
            {/* Подсказка для полного режима */}
            {!useTimeRange && (
              <Box sx={{ 
                marginTop: '12px',
                padding: '12px 16px', 
                backgroundColor: areCourtsClosed ? '#fff3e0' : '#e8f5e8',
                border: `2px solid ${areCourtsClosed ? '#ff9800' : '#4caf50'}`,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                sx: {
                  ...pulseAnimation,
                  animation: 'pulse 2s infinite'
                }
              }}>
                <Box sx={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: areCourtsClosed ? '#ff9800' : '#4caf50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>
                  {areCourtsClosed ? '🔒' : '🔓'}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#333', fontWeight: '500', marginBottom: '4px' }}>
                    {areCourtsClosed 
                      ? `Блокировка с ${new Date().getHours().toString().padStart(2, '0')}:00 до завтра`
                      : `Открытие с ${new Date().getHours().toString().padStart(2, '0')}:00`
                    }
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic' }}>
                    {areCourtsClosed 
                      ? 'Завтра в 07:00 корты автоматически откроются'
                      : 'Корты останутся открытыми до следующей блокировки'
                    }
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
          
          {useTimeRange && (
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
              <Typography variant="body2" sx={{ marginBottom: '12px', color: '#666', fontStyle: 'italic' }}>
                Время устанавливается по часам (минуты не учитываются)
              </Typography>
              
              {/* Визуальная подсказка о времени */}
              <Box sx={{ 
                marginBottom: '16px', 
                padding: '12px 16px', 
                backgroundColor: areCourtsClosed ? '#fff3e0' : '#e8f5e8',
                border: `2px solid ${areCourtsClosed ? '#ff9800' : '#4caf50'}`,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <Box sx={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: areCourtsClosed ? '#ff9800' : '#4caf50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>
                  {areCourtsClosed ? '🔒' : '🔓'}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#333', fontWeight: '500', marginBottom: '4px' }}>
                    {areCourtsClosed 
                      ? `Блокировка с ${blockStartTime.getHours().toString().padStart(2, '0')}:00 до ${blockEndTime.getHours().toString().padStart(2, '0')}:00`
                      : `Открытие с ${blockStartTime.getHours().toString().padStart(2, '0')}:00 до ${blockEndTime.getHours().toString().padStart(2, '0')}:00`
                    }
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic' }}>
                    {areCourtsClosed 
                      ? `В ${blockEndTime.getHours().toString().padStart(2, '0')}:00 корты автоматически откроются`
                      : `В ${blockEndTime.getHours().toString().padStart(2, '0')}:00 корты автоматически закроются`
                    }
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" sx={{ marginBottom: '8px' }}>
                    Дата и час начала
                  </Typography>
                  <DatePicker
                    value={blockStartDate}
                    onChange={(newDate) => newDate && setBlockStartDate(newDate)}
                    format="dd.MM.yyyy"
                    sx={{ width: '100%', marginBottom: '12px' }}
                  />
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(6, 1fr)', 
                    gap: 1,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '8px',
                    backgroundColor: '#fafafa'
                  }}>
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = i.toString().padStart(2, '0');
                      const isSelected = blockStartTime.getHours() === i;
                      return (
                        <Button
                          key={i}
                          variant={isSelected ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => {
                            const newTime = new Date(blockStartTime);
                            newTime.setHours(i, 0, 0, 0);
                            setBlockStartTime(newTime);
                          }}
                          sx={{
                            minWidth: '40px',
                            height: '32px',
                            fontSize: '12px',
                            backgroundColor: isSelected ? '#1976d2' : 'transparent',
                            color: isSelected ? '#fff' : '#666',
                            borderColor: isSelected ? '#1976d2' : '#e0e0e0',
                            '&:hover': {
                              backgroundColor: isSelected ? '#1565c0' : '#f5f5f5',
                            }
                          }}
                        >
                          {hour}:00
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" sx={{ marginBottom: '8px' }}>
                    Дата и час окончания
                  </Typography>
                  <DatePicker
                    value={blockEndDate}
                    onChange={(newDate) => newDate && setBlockEndDate(newDate)}
                    format="dd.MM.yyyy"
                    sx={{ width: '100%', marginBottom: '12px' }}
                  />
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(6, 1fr)', 
                    gap: 1,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '8px',
                    backgroundColor: '#fafafa'
                  }}>
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = i.toString().padStart(2, '0');
                      const isSelected = blockEndTime.getHours() === i;
                      return (
                        <Button
                          key={i}
                          variant={isSelected ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => {
                            const newTime = new Date(blockEndTime);
                            newTime.setHours(i, 0, 0, 0);
                            setBlockEndTime(newTime);
                          }}
                          sx={{
                            minWidth: '40px',
                            height: '32px',
                            fontSize: '12px',
                            backgroundColor: isSelected ? '#1976d2' : 'transparent',
                                                         color: isSelected ? '#fff' : '#666',
                            borderColor: isSelected ? '#1976d2' : '#e0e0e0',
                            '&:hover': {
                              backgroundColor: isSelected ? '#1565c0' : '#f5f5f5',
                            }
                          }}
                        >
                          {hour}:00
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </LocalizationProvider>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsCoverDialogOpen(false);
              resetDialogState();
            }}
            disabled={isLoading}
            sx={{
              color: '#666',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            Отмена
          </Button>
          <Button
            onClick={async () => {
              await handleCoverCourts();
              resetDialogState();
            }}
            variant="contained"
            color="primary"
            disabled={isLoading}
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            {isLoading ? 'Обработка...' : 'Подтвердить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbar-root': {
            top: '24px'
          }
        }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ 
            width: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            borderRadius: '8px'
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Courts;